import { Analytics } from '@cobliteam/analytics';
import type { InviteForm } from '@cobliteam/api-lib/src/__generated__/graphql';
import { UserRole } from '@cobliteam/common-types';
import useInvite from '@cobliteam/hooks/src/useEntity/useInvite';
import { useTranslation } from '@cobliteam/i18n';
import Button from '@cobliteam/ui/components/Button';
import Typography from '@cobliteam/ui/components/Typography';
import Input from '@cobliteam/ui/form/Input';
import { Check } from 'lucide-react';
import { useState } from 'react';

interface UsersStepBodyProps {
  fleetId?: string;
  /** Called after the invite is sent, to advance to the next step. */
  onCompleted?: () => void;
}

/** Linger after success so the user sees the confirmation before advancing. */
const ADVANCE_DELAY_MS = 900;

/** Inline "invite a user" — email + send, with the manager role by default. */
const UsersStepBody = ({ fleetId, onCompleted }: UsersStepBodyProps) => {
  const { t } = useTranslation('basic-value-onboarding');
  const [email, setEmail] = useState('');
  const { mutateAsync: sendInvites, isPending, isSuccess } = useInvite().useSendInvites();

  const handleInvite = async () => {
    const trimmed = email.trim();
    if (!trimmed) return;
    const invite: InviteForm = { email_address: trimmed, role: UserRole.MANAGER, groups_restriction: [] };
    await sendInvites([invite]);
    Analytics.track('BasicValueModalItemCreated', { fleetId, feature: 'users' });
    window.setTimeout(() => onCompleted?.(), ADVANCE_DELAY_MS);
  };

  if (isSuccess) {
    return (
      <Typography variant='text-sm' className='flex items-center gap-1 text-success'>
        <Check size={16} aria-hidden />
        {t('inline.invited')}
      </Typography>
    );
  }

  return (
    <div className='flex items-end gap-2'>
      <Input className='flex-1' type='email' value={email} onChange={(event) => setEmail(event.target.value)} placeholder={t('inline.user_email')} />
      <Button loading={isPending} disabled={!email.trim()} onClick={handleInvite}>
        {t('inline.invite')}
      </Button>
    </div>
  );
};

export default UsersStepBody;
